import {
    useContext,
    useState,
    useMemo,
    createContext,
    ReactNode,
    useEffect,
} from "react";

import { useMsal } from "@azure/msal-react";

import {
    IWorkflowFilter,
    IWorkflowType,
    WorkflowType,
} from "./interfaces/IWorkflowContext";

import { IPagination } from "../components/IPagination";

import GenericUtils from "../utils/GenericUtils";
import WorkflowService from "../service/Docente/WorkflowService";

interface WorkflowProviderProps {
    children: ReactNode;
}

interface WorkflowContextData {
    workflows: WorkflowType[];
    workflowTypes: IWorkflowType[];
    workflowFilter: IWorkflowFilter;
    pagination: IPagination;
    filterWorkflows: (filter: IWorkflowFilter) => Promise<void>;
    setWorkflowFilter: (filter: IWorkflowFilter) => void;
}

export const WorkflowContext = createContext<WorkflowContextData>(
    {} as WorkflowContextData
);

export function WorkflowProvider({ children }: WorkflowProviderProps) {
    const [workflows, setWorkflows] = useState<WorkflowType[]>([]);
    const [pagination, setPagination] = useState<IPagination>(
        {} as IPagination
    );
    const [workflowTypes, setWorkflowTypes] = useState<IWorkflowType[]>([]);
    const [workflowFilter, setWorkflowFilter] = useState<IWorkflowFilter>(
        {} as IWorkflowFilter
    );

    const { instance } = useMsal();

    const BLOCKED_TACH_ID = 1;
    const BLOCKED_LEGAL_ALLOWANCE = 2;
    const BLOCKED_OVERTIME = 10;

    const filterWorkflows = async (filter: IWorkflowFilter) => {
        setWorkflows([]);

        const filtered = { ...filter };

        if (pagination.sizePage === 1 && pagination.actualPage > 0) {
            filtered.actualPage = pagination.actualPage - 1;
            setWorkflowFilter(filtered);
        }

        const response = await WorkflowService.getWorkflowsByFilter(
            filtered,
            instance
        );

        if (GenericUtils.isSuccessStatus(response)) {
            let responseWorkflows;
            const AGUARDANDO_APROVACAO = "AGUARDANDO_APROVACAO";
            if (
                GenericUtils.getUser().isCord &&
                (filter.workflowTypeId.toString() === "3" ||
                    filter.workflowTypeId.toString() === "5") &&
                filter.status === AGUARDANDO_APROVACAO
            ) {
                responseWorkflows = response.data.processos.filter(
                    (linha: { docente: { matriculaAdp: string } }) =>
                        linha.docente.matriculaAdp !==
                        GenericUtils.getUser().docente.matriculaAdp
                );
            } else {
                responseWorkflows = response.data.processos;
            }

            const responsePagination =
                response.data.meta || ({} as IPagination);

            setWorkflows(responseWorkflows);
            setPagination(responsePagination);
        } else {
            setWorkflows([]);
        }
    };

    const handleValidateWorkflowTypes = (
        workflowTypesData: IWorkflowType[]
    ) => {
        let filteredWorkflow = workflowTypesData.filter(
            (workflow: IWorkflowType) =>
                workflow.workflowId !== BLOCKED_TACH_ID &&
                workflow.workflowId !== BLOCKED_OVERTIME
        );

        if (!GenericUtils.getUser().isDac) {
            filteredWorkflow = filteredWorkflow.filter(
                (workflow: IWorkflowType) =>
                    workflow.workflowId !== BLOCKED_LEGAL_ALLOWANCE
            );
        }

        return filteredWorkflow;
    };

    const getWorkflowTypes = async () => {
        const response = await WorkflowService.getWorkflowTypes(instance);

        if (GenericUtils.isSuccessStatus(response)) {
            const responseWorkflowTypes = handleValidateWorkflowTypes(
                response.data
            );
            const initialWorkflowFilter: IWorkflowFilter = {
                workflowTypeId: 0,
                workflowTypeName: "",
                status: "AGUARDANDO_APROVACAO",
                actualPage: 0,
                pageSize: 10,
            };

            setWorkflowTypes(responseWorkflowTypes);
            setWorkflowFilter(initialWorkflowFilter);
        }
    };

    useEffect(() => {
        getWorkflowTypes();
    }, []);

    const contextValue = useMemo(
        () => ({
            workflows,
            workflowTypes,
            workflowFilter,
            pagination,
            filterWorkflows,
            setWorkflowFilter,
        }),
        [workflows, workflowTypes, workflowFilter, pagination]
    );

    return (
        <WorkflowContext.Provider value={contextValue}>
            {children}
        </WorkflowContext.Provider>
    );
}

WorkflowContext.displayName = "Workflow";

export function useWorkflow() {
    return useContext(WorkflowContext);
}
